const DELIVERY_CLASS = '.subscription-plans__fieldset--delivery';
const BILLING_CLASS = '.subscription-plans__fieldset--billing';
const INPUT_CLASS = '.subscription-plans__fieldset__input';

const PERIOD_DISPLAYS = {
  monthly: 'month',
  quarterly: 'quarter',
  biannually: 'half-year',
  annually: 'year',
};

const MONTHS_PER_PERIOD = {
  monthly: 1,
  quarterly: 3,
  biannually: 6,
  annually: 12,
};

class ProductSubscriptionPlans extends HTMLElement {
  country = 'United States';
  billingType = 'monthly';
  deliveryType = 'monthly';
  sellingPlanId = null;
  productFormId = null;
  price = 0;
  variantPrice = 0;
  priceAdjustment = 0;
  adjustedPrice = 0;
  monthlyDeliveryMonthlyBilling = null;
  monthlyDeliveryBiannualBilling = null;
  monthlyDeliveryAnnualBilling = null;
  quarterlyDeliveryQuarterlyBilling = null;
  quarterlyDeliveryBiannualBilling = null;
  quarterlyDeliveryAnnualBilling = null;
  planMapping = {};
  productAddOns = {};
  iconCheck = null;
  iconX = null;
  currencySymbol = '$';
  languageCode = 'en';
  currencyCode = 'USD';
  countryCode = 'US';
  clubLaunchDate = null;

  constructor() {
    super();
    this.getData();
    this.getIcons();
    this.updateDiscountPercents();
    this.updatePerks();
    this.updatePricing();
    this.updateMergeTokens();
    this.handleBillingChange = this.handleBillingChange.bind(this);
    this.handleDeliveryChange = this.handleDeliveryChange.bind(this);
    this.querySelector(DELIVERY_CLASS)?.addEventListener('change', this.handleDeliveryChange);
    this.querySelector(BILLING_CLASS)?.addEventListener('change', this.handleBillingChange);
    this.handleChange();
  }

  getIcons() {
    const iconCheck = this.querySelector('.media-hero__icon.icon-checkmark-alt');
    const iconX = this.querySelector('.media-hero__icon.icon-close-alt');
    this.iconCheck = iconCheck.outerHTML;
    this.iconX = iconX.outerHTML;
  }

  getData() {
    this.productFormId = this.dataset.productFormId;
    const variantPrice = parseInt(this.dataset.variantPrice || 0);
    const comparePrice = parseInt(this.dataset.comparePrice || 3000);
    this.productAddOns = JSON.parse(this.dataset.productAddOns);
    this.country = this.dataset.country;
    this.currencySymbol = this.dataset.currencySymbol;
    this.currencyCode = this.dataset.currencyCode;
    this.languageCode = this.dataset.languageCode;
    this.countryCode = this.dataset.countryCode;
    this.clubLaunchDate = this.dataset.clubLaunchDate || null;

    this.planMapping = {
      monthly: {
        monthly: {
          id: this.dataset.monthlyDeliveryMonthlyBilling,
          name: this.dataset.monthlyDeliveryMonthlyBillingName,
          price: comparePrice,
          adjusted: this.dataset.monthlyDeliveryMonthlyBillingPriceAdjusted,
          variantPrice,
        },
        quarterly: {
          id: '',
          name: '',
          price: '',
          adjusted: '',
          variantPrice: '',
        },
        biannually: {
          id: this.dataset.monthlyDeliveryBiannualBilling,
          name: this.dataset.monthlyDeliveryBiannualBillingName,
          price: comparePrice * 6,
          adjusted: this.dataset.monthlyDeliveryBiannualBillingPriceAdjusted,
          variantPrice,
        },
        annually: {
          id: this.dataset.monthlyDeliveryAnnualBilling,
          name: this.dataset.monthlyDeliveryAnnualBillingName,
          price: comparePrice * 12,
          adjusted: this.dataset.monthlyDeliveryAnnualBillingPriceAdjusted,
          variantPrice,
        },
      },
      quarterly: {
        monthly: {
          id: '',
          name: '',
          price: '',
          adjusted: '',
          variantPrice: '',
        },
        quarterly: {
          id: this.dataset.quarterlyDeliveryQuarterlyBilling,
          name: this.dataset.quarterlyDeliveryQuarterlyBillingName,
          price: comparePrice,
          adjusted: this.dataset.quarterlyDeliveryQuarterlyBillingPriceAdjusted,
          variantPrice,
        },
        biannually: {
          id: this.dataset.quarterlyDeliveryBiannualBilling,
          name: this.dataset.quarterlyDeliveryBiannualBillingName,
          price: comparePrice * 2,
          adjusted: this.dataset.quarterlyDeliveryBiannualBillingPriceAdjusted,
          variantPrice,
        },
        annually: {
          id: this.dataset.quarterlyDeliveryAnnualBilling,
          name: this.dataset.quarterlyDeliveryAnnualBillingName,
          price: comparePrice * 4,
          adjusted: this.dataset.quarterlyDeliveryAnnualBillingPriceAdjusted,
          variantPrice,
        },
      },
    };
  }

  handleDeliveryChange(event) {
    this.deliveryType = event.target.value;
    if (this.deliveryType === 'monthly' && this.billingType === 'quarterly') {
      this.billingType = 'monthly';
    } else if (this.deliveryType === 'quarterly' && this.billingType === 'monthly') {
      this.billingType = 'quarterly';
    }
    this.handleChange();
  }

  handleBillingChange(event) {
    this.billingType = event.target.value;
    this.handleChange();
  }

  handleChange() {
    this.clearUnselected(DELIVERY_CLASS, this.deliveryType);
    this.clearUnselected(BILLING_CLASS, this.billingType);
    this.hideInvalidPlans();
    this.setSellingPlanId();
    this.updateProductForm();
    this.updatePricing();
    this.updatePerks();
    this.togglePerkImage();
    this.updateMergeTokens();
    this.updateDiscountPercents();
    this.toggleCountdown();
  }

  /**
   * Sets all other inputs in the parent to unchecked if they are not the selected value
   * @param {string} parentSelector The parent selector to search for inputs
   * @param {string} selectedValue The value of the input that should remain checked
   */
  clearUnselected(parentSelector, selectedValue) {
    const elements = this.querySelector(parentSelector)?.querySelectorAll(INPUT_CLASS);
    if (elements) {
      elements.forEach((element) => {
        if (element.value !== selectedValue) {
          element.checked = false;
        } else {
          element.checked = true;
        }
      });
    }
  }

  /**
   * Sets the selling plan ID based on the selected delivery and billing types
   */
  setSellingPlanId() {
    this.sellingPlanId = this.planMapping[this.deliveryType]?.[this.billingType]?.id || null;
  }

  /**
   * Updates the quick-add product form with the selected selling plan
   */
  updateProductForm() {
    const productForm = this.parentElement.querySelector(`#${this.productFormId}`);
    if (productForm) {
      const sellingPlanEl = productForm.querySelector('[name="items[0][selling_plan]"]');
      if (sellingPlanEl) {
        sellingPlanEl.value = this.sellingPlanId;
        sellingPlanEl.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /**
   * Builds the HTML for a perk element
   * @param {object} perk The perk object to be rendered
   * @param {HTMLElement} parentEl The parent element to attach the perk to
   * @returns {HTMLElement} The updated parent element with the perk attached
   */
  buildPerkElement(perk, parentEl) {
    const disabled = perk.quantity === 0;
    if (!('highlight_label' in perk) || !perk.highlight_label) {
      const emptyEl = document.createElement('span');
      emptyEl.style.display = 'none';
      parentEl.appendChild(emptyEl);
      return parentEl;
    }

    const perkEl = document.createElement('div');
    perkEl.classList.add('media-hero__highlight-wrapper');
    const itemEl = document.createElement('li');
    itemEl.classList.add('media-hero__highlight');
    const iconWrapperEl = document.createElement('div');
    iconWrapperEl.classList.add('media-hero__icon-wrapper');
    iconWrapperEl.innerHTML = disabled ? this.iconX : this.iconCheck;
    itemEl.appendChild(iconWrapperEl);
    const labelEl = document.createElement('div');
    labelEl.classList.add('media-hero__highlight-label', 'font-body-1');
    labelEl.innerHTML = `${disabled ? '<s>' : ''}${perk.highlight_label}${disabled ? '</s>' : ''}`;
    itemEl.appendChild(labelEl);
    perkEl.appendChild(itemEl);
    if (disabled) {
      itemEl.classList.add('disabled');
      const tooltipEl = document.createElement('span');
      tooltipEl.classList.add('media-hero__highlight-label__tooltip');
      tooltipEl.textContent = `This item is not available for your selected subscription plan`;
      perkEl.appendChild(tooltipEl);
    }
    parentEl.appendChild(perkEl);
    return parentEl;
  }

  /**
   * Resets the perks list based on the selected subscription plan
   */
  updatePerks() {
    const perksHeadingEl = this.parentElement.querySelector('#included-perks');
    const perksEl = this.parentElement.querySelector('.media-hero__subscription-perks.media-hero__highlights');
    if (perksEl) {
      const planName = this.planMapping[this.deliveryType]?.[this.billingType]?.name;
      if (!planName || !(planName in this.productAddOns)) {
        perksHeadingEl.style.display = 'none';
        perksEl.innerHTML = '<span style="display:none;"></span>';
        return;
      }
      perksHeadingEl.style.display = 'block';
      const perks = this.productAddOns[planName];
      const newPerksEl = document.createElement('ul');
      newPerksEl.classList.add('media-hero__subscription-perks', 'media-hero__highlights', 'no-divider');
      perksEl.replaceWith(perks.reduce((acc, perk) => this.buildPerkElement(perk, acc), newPerksEl));
    }
  }

  /**
   * Toggles the perk image to display if semiannual or annual is selected
   */
  togglePerkImage() {
    this.getRootNode()
      .querySelectorAll('.media-hero__image-wrapper--perk')
      .forEach((el) => {
        if (this.deliveryType === 'monthly' && this.billingType === 'annually') {
          el.classList.remove('hide');
        } else {
          el.classList.add('hide');
        }
      });
  }

  /**
   * Calculates the price adjustment percentage
   * @param {number} price
   * @param {number} adjustedPrice
   * @returns {number} The price adjustment percentage
   */
  calculateAdjustment(price, adjustedPrice) {
    if (price && adjustedPrice && price !== adjustedPrice) {
      return (price - adjustedPrice) / price;
    }
    return 0;
  }

  /**
   * Returns a formatted price string
   * @param {number} price The price to format
   * @returns {string} The formatted price string
   */
  formatPrice(price) {
    const p = (price / 100).toFixed(2);
    try {
      const locale = `${this.languageCode}-${this.countryCode}`;
      const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency: this.currencyCode });
      return formatter.format(p);
    } catch {
      return `${this.currencySymbol}${p}`;
    }
  }

  /**
   * Updates the pricing elements based on the selected delivery and billing types
   */
  updatePricing() {
    this.price = parseInt(this.planMapping[this.deliveryType]?.[this.billingType]?.price || 0);
    this.variantPrice = parseInt(this.planMapping[this.deliveryType]?.[this.billingType]?.variantPrice || 0);
    this.adjustedPrice = parseInt(this.planMapping[this.deliveryType]?.[this.billingType]?.adjusted || 0);
    this.priceAdjustment = this.calculateAdjustment(this.price, this.adjustedPrice);

    const originalPriceEl = this.parentElement.querySelector('.media-hero__product-pricing__original');
    const adjustedPriceEl = this.parentElement.querySelector('.media-hero__product-pricing__adjusted');

    if (originalPriceEl) {
      originalPriceEl.innerHTML = this.formatPrice(
        this.price / (MONTHS_PER_PERIOD[this.billingType] / MONTHS_PER_PERIOD[this.deliveryType])
      );
      originalPriceEl.style.display = this.priceAdjustment > 0 ? 'inline' : 'none';
    }
    if (adjustedPriceEl) {
      if (this.billingType === 'monthly' && this.deliveryType === 'monthly' && this.priceAdjustment > 0) {
        adjustedPriceEl.innerHTML = `${this.formatPrice(this.adjustedPrice)} first ${
          PERIOD_DISPLAYS[this.deliveryType]
        }`;
      } else {
        adjustedPriceEl.innerHTML = `${this.formatPrice(
          this.adjustedPrice / (MONTHS_PER_PERIOD[this.billingType] / MONTHS_PER_PERIOD[this.deliveryType])
        )} / ${PERIOD_DISPLAYS[this.deliveryType]}`;
      }
    }
  }

  /**
   * Updates the discount percentage element for a given billing type
   * @param {HTMLElement} el The element to update
   * @param {string} billingType The billing type to use for the discount calculation
   */
  updateDiscountPercent(el, billingType) {
    if (el) {
      const price = parseInt(this.planMapping[this.deliveryType]?.[billingType]?.price || 0);
      const adjustedPrice = parseInt(this.planMapping[this.deliveryType]?.[billingType]?.adjusted || 0);
      const adjustment = this.calculateAdjustment(price, adjustedPrice);
      if (adjustment > 0) {
        el.innerHTML = `(${Math.floor(adjustment * 100)}% off)`;
      } else {
        el.innerHTML = '';
      }
    }
  }

  /**
   * Updates the discount percentages for all billing types
   */
  updateDiscountPercents() {
    const biannualEl = this.querySelector(INPUT_CLASS + '--biannual-billing + label span');
    this.updateDiscountPercent(biannualEl, 'biannually');
    const annualEl = this.querySelector(INPUT_CLASS + '--annual-billing + label span');
    this.updateDiscountPercent(annualEl, 'annually');
  }

  /**
   * Hides plans that are not available based on the selected delivery and billing types
   */
  hideInvalidPlans() {
    if (this.deliveryType === 'monthly') {
      this.togglePlanDisplay(false, INPUT_CLASS + '--quarterly-billing');
      this.togglePlanDisplay(!!this.planMapping['monthly']['annually'].id, INPUT_CLASS + '--annual-billing');
      this.togglePlanDisplay(!!this.planMapping['monthly']['biannually'].id, INPUT_CLASS + '--biannual-billing');
      this.togglePlanDisplay(!!this.planMapping['monthly']['monthly'].id, INPUT_CLASS + '--monthly-billing');
    } else {
      this.togglePlanDisplay(false, INPUT_CLASS + '--monthly-billing');
      this.togglePlanDisplay(!!this.planMapping['quarterly']['annually'].id, INPUT_CLASS + '--annual-billing');
      this.togglePlanDisplay(!!this.planMapping['quarterly']['biannually'].id, INPUT_CLASS + '--biannual-billing');
      this.togglePlanDisplay(!!this.planMapping['quarterly']['quarterly'].id, INPUT_CLASS + '--quarterly-billing');
    }

    if (this.billingType === 'biannually') {
      this.togglePlanDisplay(!!this.planMapping['monthly']['biannually'].id, INPUT_CLASS + '--monthly-delivery');
      this.togglePlanDisplay(!!this.planMapping['quarterly']['biannually'].id, INPUT_CLASS + '--quarterly-delivery');
    } else {
      this.togglePlanDisplay(!!this.planMapping['monthly']['annually'].id, INPUT_CLASS + '--monthly-delivery');
      this.togglePlanDisplay(!!this.planMapping['quarterly']['annually'].id, INPUT_CLASS + '--quarterly-delivery');
    }
  }

  /**
   * Toggles visibility of elements based on the show parameter
   * @param {boolean} show Whether to show or hide the elements
   * @param {string} selector The selector of the input to be toggled
   */
  togglePlanDisplay(show, selector) {
    const elements = this.querySelectorAll(`${selector}, ${selector} + label`);
    elements.forEach((element) => {
      element.style.display = show ? 'block' : 'none';
    });
  }

  /**
   * Updates all instances of a merge token in the parent element
   * @param {string} name The name of the token to update
   * @param {string} value The new value of the token
   */
  updateMergeToken(name, value) {
    const tokens = this.parentElement.querySelectorAll(`.token.${name}`);
    tokens.forEach((token) => {
      token.innerHTML = value;
    });
  }

  /**
   * Updates all merge tokens in the parent element
   */
  updateMergeTokens() {
    this.updateMergeToken('delivery-period', PERIOD_DISPLAYS[this.deliveryType]);
    this.updateMergeToken('billing-period', PERIOD_DISPLAYS[this.billingType]);
    this.updateMergeToken('delivery-frequency', this.deliveryType);
    this.updateMergeToken('billing-frequency', this.billingType);
    this.updateMergeToken('discount-percent', `${Math.floor(this.priceAdjustment * 100)}%`);
    this.updateMergeToken('this-price', this.formatPrice(this.adjustedPrice));
    this.updateMergeToken(
      'next-price',
      this.billingType === 'quarterly' || this.billingType === 'monthly'
        ? this.formatPrice(this.variantPrice)
        : this.formatPrice(this.adjustedPrice)
    );

    const today = new Date();
    let thisMonth;
    if (today.getDate() < 15) {
      thisMonth = new Date(new Date().setDate(15));
      thisMonth = new Date(thisMonth.setMonth(thisMonth.getMonth() - 1));
    } else {
      thisMonth = new Date(new Date().setDate(15));
    }

    // Check if club launch date is current month or after previous month's 15th
    let extraMonth = 0;
    if (this.clubLaunchDate) {
      const launchDateParts = this.clubLaunchDate.split('-');
      const launchDate = new Date(
        parseInt(launchDateParts[0]),
        parseInt(launchDateParts[1]) - 1,
        parseInt(launchDateParts[2])
      );
      launchDate.setHours(0, 0, 0, 0);

      const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      currentMonthStart.setHours(0, 0, 0, 0);
      const currentMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      currentMonthEnd.setHours(23, 59, 59, 999);
      const previousMonth15th = new Date(today.getFullYear(), today.getMonth() - 1, 15);
      previousMonth15th.setHours(23, 59, 59, 999);

      // If launch date is in current month (between current month start and end)
      // OR if launch date is after previous month's 15th, add an extra month
      const isCurrentMonth = launchDate >= currentMonthStart && launchDate <= currentMonthEnd;
      const isAfterPreviousMonth15th = launchDate > previousMonth15th;

      if (isCurrentMonth || isAfterPreviousMonth15th) {
        extraMonth = 1;
      }
    }

    const nextBillingMonth = new Date(
      new Date(thisMonth).setMonth(thisMonth.getMonth() + MONTHS_PER_PERIOD[this.billingType] + extraMonth)
    );
    const nextDeliveryMonth = new Date(
      new Date(thisMonth).setMonth(thisMonth.getMonth() + MONTHS_PER_PERIOD[this.billingType] + 1 + extraMonth)
    );

    this.updateMergeToken(
      'next-billing-month',
      nextBillingMonth.toLocaleString('default', { month: 'long', day: 'numeric', year: 'numeric' })
    );
    this.updateMergeToken(
      'next-delivery-month',
      nextDeliveryMonth.toLocaleString('default', { month: 'long', day: 'numeric', year: 'numeric' })
    );
  }

  /**
   * Toggles visibility of the countdown label based on the selected delivery and billing types
   */
  toggleCountdown() {
    const countdownEls = this.parentElement.querySelectorAll('.media-hero__countdown, .countdown');
    countdownEls.forEach((countdownEl) => {
      countdownEl.style.display = this.billingType === 'monthly' && this.deliveryType === 'monthly' ? 'block' : 'none';
    });
  }
}

if (!customElements.get('product-subscription-plans')) {
  customElements.define('product-subscription-plans', ProductSubscriptionPlans);
}
